import { flexRender } from '@tanstack/react-table'
import { TableProps } from './types'
import Pagination from '../pagination'
import { TableBody } from '../table-body'
import { table as tableEL } from './config'
import { TableHead } from '../table-head'
import { usePagination, useTable } from '../../hooks'
import { useContextTableMenu } from '../../hooks/use-context'
import { ContextMenu } from '../context-menu'
import Spinner from '~/shared/ui/spinner'

export const Table = <T, K extends keyof T>({
  data = [],
  columns = [],
  sortConfig,
  handleSort,
  sortEnabled,
  className,
  pagination,
  rowContextMenu,
  onRowClick,
  loadMore,
  fixedWidth,
  rounded,
  aroundwithout,
  hasSubRowContent,
  subRowComponent,
  isSubRowOpen,
  variant = 'default',
  variantRow,
  border,
  alignBodyStyle,
  colorizeCell,
  textSize,
  allowZeroStockClick = false,
  enableRowHoverTracking = true,
  getRowUrl,
  getRowClassName,
}: TableProps<T, K>) => {
  // Define React Table
  const { table } = useTable({ data, columns, pagination })
  // Pagination
  const {
    firstPage,
    isPreviousPage,
    previousPage,
    nextPage,
    isNextPage,
    lastPage,
    currentPage,
    allPages,
    pageSize,
    handlePagination,
  } = usePagination({ table })

  // Context
  const { anchorPoint, isShown, row } = useContextTableMenu<T>()

  // Table Data
  const dataSource = table.getRowModel().rows
  const columnsData = table.getHeaderGroups()
  const footerGroups = table.getFooterGroups()
  const visibleFooterGroups =
    dataSource.length > 0
      ? footerGroups.filter((footerGroup) =>
          footerGroup.headers.some((header) => {
            if (header.isPlaceholder || !header.column.columnDef.footer) {
              return false
            }

            const content = flexRender(
              header.column.columnDef.footer,
              header.getContext(),
            )

            return (
              content !== null && content !== undefined && content !== false
            )
          }),
        )
      : []

  const hasFooter = dataSource.length > 1 && visibleFooterGroups.length > 0

  return (
    <div>
      <table
        className={fixedWidth ? tableEL({ className, rounded }) : className}
      >
        <TableHead
          columns={columnsData}
          aroundwithout={aroundwithout}
          sortConfig={sortConfig}
          handleSort={handleSort}
          sortEnabled={sortEnabled}
          border={border}
          variant={variant}
        />
        <TableBody
          textSize={textSize}
          data={dataSource}
          variantRow={variantRow}
          rounded={rounded}
          onRowClick={onRowClick}
          hasSubRowContent={hasSubRowContent}
          subRowComponent={subRowComponent}
          isSubRowOpen={isSubRowOpen}
          cellsCount={columnsData[0].headers.length}
          alignBodyStyle={alignBodyStyle}
          colorizeCell={colorizeCell}
          disableWhitespaceWrap
          allowZeroStockClick={allowZeroStockClick}
          enableRowHoverTracking={enableRowHoverTracking}
          getRowUrl={getRowUrl}
          getRowClassName={getRowClassName}
        />
        {hasFooter ? (
          <tfoot className="border-t border-gray-200 bg-gray-50 font-semibold text-gray-800">
            {visibleFooterGroups.map((footerGroup) => (
              <tr key={footerGroup.id}>
                {footerGroup.headers.map((header, index) => {
                  const isFirstHeader = index === 0
                  const isLastHeader = index === footerGroup.headers.length - 1

                  return (
                    <td
                      key={header.id}
                      colSpan={header.colSpan}
                      className={`py-3 ${
                        isFirstHeader ? 'px-5' : 'px-3'
                      } ${isLastHeader ? 'rounded-br-md' : ''}`}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.footer,
                            header.getContext(),
                          )}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tfoot>
        ) : null}
      </table>{' '}
      {loadMore ? (
        <div className="flex justify-center my-5">
          <Spinner />
        </div>
      ) : null}
      <ContextMenu
        top={anchorPoint.y}
        left={anchorPoint.x}
        isShown={isShown}
        items={rowContextMenu}
        row={row}
      />
      {pagination ? (
        <div className="flex items-center gap-2">
          <Pagination
            allPages={allPages}
            currentPage={currentPage}
            firstPage={firstPage}
            nextPage={nextPage}
            lastPage={lastPage}
            previousPage={previousPage}
            isNextPage={isNextPage}
            isPreviousPage={isPreviousPage}
            pageSize={pageSize}
            handlePagination={handlePagination}
          />
        </div>
      ) : null}
    </div>
  )
}

export default Table
